function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer, Container } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters } from "../../components/analytics-kit";
import Error from "../../components/error";
import { Loader } from "../../components/loaders";
import { KpiStrip, Kpi } from "../../components/card";
import { hasTenantFeature, adBillingMode, hasCampaignDashboards, campaignsDashboardOnly } from "../../functions/tenant-features";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip } from "@progress/kendo-react-charts";
import ChartBoundary from "../market-intelligence/chart-boundary";
import PostalBubbleMap from "../market-intelligence/postal-bubble-map";
import MI from "../../endpoints/market-intelligence";
import { normalizeLocations } from "../market-intelligence/normalize";
import ServiceFactory from "../../service";
import { adSvc, channelLabel, num, usd, pct, rangeQs, isActiveStatus, setCampaignStatus } from "./common";
import MonthsChart from "./months-chart";
import CampaignsNav, { DeltaTag, kpiDelta, campaignTitle, ribbonDelta, AutopilotStrip } from "./nav";
// Global comparison (top-right "Trend Comparison") → dashboard compare token
function compareOf(comp) {
  return comp === "prev_year" ? "previous_year" : comp === "none" ? "none" : "previous_period";
}

/*
 * Campaigns → Overview (CAMPAIGNS_DASHBOARD_REDESIGN.md §3.1, phase 1).
 * One read: GET /v1/campaign/ad/overview?range&compare → KPIs + compare,
 * per-channel, per-market, daily series (× channel), top campaigns, health.
 * Money tiles/columns only when the tenant is money-visible (§2.5); channel
 * labels only (§2.6); CTR is impressions-weighted and excludes video buys.
 */

const METRICS = [["impressions", "Impressions"], ["clicks", "Clicks"], ["videoViews", "Video views"], ["spend", "Spend"]];
const CHANNEL_COLORS = {
  Display: "var(--plk-chart-display)",
  Retarget: "var(--plk-chart-retarget)",
  Search: "var(--plk-chart-search)",
  Social: "var(--plk-chart-social)",
  Video: "var(--plk-chart-video)",
  YouTube: "var(--plk-chart-youtube)",
  "CTV/OTT": "var(--plk-chart-ctv)",
  Pinterest: "var(--plk-chart-pinterest)"
};
function svc(path) {
  const S = ServiceFactory.getType(path, "product");
  return new S();
}
class OverviewView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "load", async () => {
      this.setState({
        loading: true,
        error: null
      });
      try {
        const d = await adSvc(`/overview?${rangeQs(this.state.range)}&compare=${this.state.compare}`).getAsync();
        if (this._alive) this.setState({
          data: d,
          loading: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          error: e && e.message || "Could not load the overview.",
          loading: false
        });
      }
    });
    _defineProperty(this, "loadInsights", () => {
      ["campaign-stats-analysis", "ad-asset-stats-analysis"].forEach(t => {
        svc(`/sales/insights/${t}/${this.insightRange()}`).getAsync().then(d => this._alive && this.setState({
          insights: {
            ...this.state.insights,
            [t]: d
          }
        })).catch(() => {});
      });
    });
    _defineProperty(this, "generateInsight", async t => {
      this.setState({
        insightBusy: {
          ...this.state.insightBusy,
          [t]: true
        }
      });
      try {
        const d = await svc(`/sales/insights/${t}/${this.insightRange()}/generate?force=true`).postAsync({});
        this.setState({
          insights: {
            ...this.state.insights,
            [t]: d
          }
        });
      } catch (e) {/* surfaced by the empty state */} finally {
        this.setState({
          insightBusy: {
            ...this.state.insightBusy,
            [t]: false
          }
        });
      }
    });
    _defineProperty(this, "toggle", async c => {
      const next = isActiveStatus(c.status) ? "Paused" : "Enabled";
      try {
        await setCampaignStatus(c.id, next);
        this.load();
      } catch (e) {/* keep */}
    });
    this.state = {
      data: null,
      error: null,
      loading: true,
      range: getGlobalRange() || "this_month",
      compare: compareOf(getGlobalComp()),
      metric: "impressions",
      locations: [],
      insights: {},
      insightBusy: {}
    };
  }
  componentDidMount() {
    this._alive = true;
    this._sub = subscribePeriod && subscribePeriod(({
      range,
      comp
    }) => this._alive && this.setState({
      range: range || this.state.range,
      compare: compareOf(comp)
    }, () => {
      this.load();
      if (this.loadInsights) this.loadInsights();
    }));
    this.load();
    MI.getLocations().then(d => this._alive && this.setState({
      locations: normalizeLocations(d) || []
    })).catch(() => {});
    this.loadInsights();
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._sub && unsubscribePeriod) unsubscribePeriod(this._sub);
  }
  insightRange() {
    const r = String(this.state.range || "");
    return /^(this_month|last_month|this_quarter|last_quarter|this_year|last_year|year|all_time)$/.test(r) ? r : "this_month";
  }
  marketRows() {
    const {
      data,
      locations
    } = this.state;
    const byName = {};
    (locations || []).forEach(l => {
      const n = l.name || l.LocationName;
      if (n) byName[String(n).toLowerCase()] = l;
    });
    return (data && data.byMarket || []).map(m => {
      // server centroid (tenant location table) first; MI locations as fallback
      const g = m.geo && Number.isFinite(Number(m.geo.lat)) && Number.isFinite(Number(m.geo.lng)) ? m.geo : byName[String(m.market).toLowerCase()];
      if (!g || !Number.isFinite(Number(g.lat)) || !Number.isFinite(Number(g.lng)) || !Number(g.lat)) return null;
      return {
        lat: Number(g.lat),
        lng: Number(g.lng),
        label: m.market,
        value: Number((m.kpis || {}).impressions) || 0,
        sub: `${num((m.kpis || {}).impressions)} impr · CTR ${pct((m.kpis || {}).ctr)}`
      };
    }).filter(Boolean);
  }
  render() {
    if (!hasCampaignDashboards()) return /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Campaigns isn't configured for this workspace."));
    const {
      data,
      error,
      loading,
      metric,
      insights,
      insightBusy
    } = this.state;
    const money = adBillingMode() !== "managed";
    // Read-only dashboard tenants (managed): no flight actions, no drill to
    // campaign/flight detail — Overview + Markets are the whole surface.
    const dashOnly = campaignsDashboardOnly();
    const k = data && data.kpis || {};
    const pk = data && data.compareKpis || null;
    const channels = [...new Set((data && data.series || []).flatMap(d => (d.byChannel || []).map(c => c.type)))];
    const series = (data && data.series || []).map(d => {
      const row = {
        date: d.date.slice(5)
      };
      channels.forEach(c => {
        const m = (d.byChannel || []).find(x => x.type === c);
        row[c] = m ? Number(m[metric]) || 0 : 0;
      });
      row.prev = null;
      return row;
    });
    if (data && data.compareSeries) data.compareSeries.forEach((d, i) => {
      if (series[i]) series[i].prev = Number(d[metric]) || 0;
    });
    const health = data && data.health || {};
    const anyVideoTop = (data && data.topCampaigns || []).some(c => Number((c.kpis || {}).videoViews) > 0);
    const mapRows = this.marketRows();
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view campaigns-view wa-view"
    }, /*#__PURE__*/React.createElement(CampaignsNav, {
      active: "overview",
      history: this.props.history,
      subtitle: `Paid media across every channel${data && data.compare ? ` · vs ${data.compare.from} – ${data.compare.to}` : " · pick a comparison in the top bar for deltas"}`,
      chips: [data && (data.topCampaigns || []).length ? {
        k: "Campaigns",
        v: num((data.topCampaigns || []).length)
      } : null],
      actions: !dashOnly ? /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: () => this.props.history.push("/Campaigns/FlightBuilder")
      }, "New flight") : null
    }), /*#__PURE__*/React.createElement(AutopilotStrip, null), error && /*#__PURE__*/React.createElement(Error, {
      message: error
    }), loading && !data && /*#__PURE__*/React.createElement(Loader, null), data && /*#__PURE__*/React.createElement(React.Fragment, null, !dashOnly && money && ((health.noDelivery || []).length > 0 || (health.endingSoon || []).length > 0 || health.pacedToday > 0) && /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning",
      style: {
        display: "flex",
        gap: 18,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 12.5
      }
    }, (health.noDelivery || []).length > 0 && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, health.noDelivery.length, " active with no delivery"), " for 48h \xB7 ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Campaigns/Flights")
    }, "review \u2192")), (health.endingSoon || []).length > 0 && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Ending within 3 days:"), " ", health.endingSoon.map(f => /*#__PURE__*/React.createElement("a", {
      key: f.id,
      style: {
        cursor: "pointer",
        marginLeft: 6
      },
      onClick: () => this.props.history.push(`/Campaigns/flight/${f.id}`)
    }, f.name))), health.pacedToday > 0 && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, health.pacedToday, " paced out today"), " \xB7 resumes after midnight"))), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: pk ? true : false,
      tiles: [{
        l: "Impressions",
        v: num(k.impressions),
        d: ribbonDelta(k.impressions, pk && pk.impressions),
        s: series.length ? `${num(Math.round((Number(k.impressions) || 0) / Math.max(1, series.length)))} per day` : null,
        sp: series.map(r => channels.reduce((a, c) => a + (Number(r[c]) || 0), 0))
      }, {
        l: "Clicks",
        v: num(k.clicks),
        d: ribbonDelta(k.clicks, pk && pk.clicks),
        s: `${pct(k.ctr)} CTR on click inventory`
      }, {
        l: "CTR",
        v: pct(k.ctr),
        d: ribbonDelta(k.ctr, pk && pk.ctr, {
          pp: true
        }),
        s: "click inventory only"
      }, {
        l: "Conversions",
        v: num(k.conversions),
        d: ribbonDelta(k.conversions, pk && pk.conversions),
        s: Number(k.revenue) > 0 ? `${usd(k.revenue)} attributed revenue` : null
      }, money ? {
        l: "Spend",
        v: usd(k.spend),
        d: ribbonDelta(k.spend, pk && pk.spend),
        s: Number(k.cpc) > 0 ? `${usd(k.cpc)} CPC` : null,
        invert: true
      } : {
        l: "Video views",
        v: num(k.videoViews),
        d: ribbonDelta(k.videoViews, pk && pk.videoViews),
        s: "YouTube and CTV"
      }, money && Number(k.roas) > 0 ? {
        l: "ROAS",
        v: `${Number(k.roas).toFixed(2)}×`,
        d: ribbonDelta(k.roas, pk && pk.roas),
        s: "attributed revenue ÷ spend"
      } : {
        l: "Video views",
        v: num(k.videoViews),
        d: ribbonDelta(k.videoViews, pk && pk.videoViews),
        s: "YouTube and CTV"
      }].filter((t, i, arr) => arr.findIndex(x => x.l === t.l) === i)
    }), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid two-one"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Trend",
      hint: "daily \xB7 stacked by channel",
      right: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, METRICS.filter(([m]) => money || m !== "spend").map(([m, l]) => /*#__PURE__*/React.createElement("button", {
        key: m,
        className: metric === m ? "on" : "",
        onClick: () => this.setState({
          metric: m
        })
      }, l)))
    }, /*#__PURE__*/React.createElement(ChartBoundary, null, /*#__PURE__*/React.createElement(Chart, {
      style: {
        height: 260
      },
      transitions: false
    }, /*#__PURE__*/React.createElement(ChartLegend, {
      position: "bottom"
    }), /*#__PURE__*/React.createElement(ChartTooltip, {
      visible: true
    }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
      categories: series.map(s => s.date),
      labels: {
        step: Math.max(1, Math.ceil(series.length / 12))
      },
      majorGridLines: {
        visible: false
      }
    })), /*#__PURE__*/React.createElement(ChartValueAxis, null, /*#__PURE__*/React.createElement(ChartValueAxisItem, {
      labels: {
        format: "{0:n0}"
      }
    })), /*#__PURE__*/React.createElement(ChartSeries, null, [...channels.map(c => /*#__PURE__*/React.createElement(ChartSeriesItem, {
      key: c,
      type: series.length > 1 ? "area" : "column",
      stack: true,
      name: channelLabel(c),
      data: series.map(s => s[c]),
      color: CHANNEL_COLORS[c] || "var(--plk-chart-stable)",
      opacity: 0.85,
      markers: {
        visible: false
      }
    })), ...(data.compareSeries ? [/*#__PURE__*/React.createElement(ChartSeriesItem, {
      key: "__prev",
      type: "line",
      name: "previous period",
      data: series.map(s => s.prev),
      color: "var(--plk-chart-previous)",
      dashType: "dash",
      markers: {
        visible: false
      }
    })] : [])]))))), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Channel mix"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Share"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CPC"))), /*#__PURE__*/React.createElement("tbody", null, (data.byChannel || []).map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.type
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "code",
      style: {
        background: CHANNEL_COLORS[c.type] || "var(--plk-chart-stable)",
        color: "#fff",
        padding: "1px 8px",
        borderRadius: 999
      }
    }, channelLabel(c.type))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, k.impressions ? `${(100 * (c.kpis.impressions || 0) / k.impressions).toFixed(0)}%` : "—"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, ["YouTube", "CTV/OTT"].includes(c.type) ? "—" : pct(c.kpis.ctr)), money && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, Number(c.kpis.cpc) > 0 ? usd(c.kpis.cpc) : "—")))))))), /*#__PURE__*/React.createElement(MonthsChart, {
      months: data.months,
      money: money
    }), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid two-one"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Markets"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gridTemplateColumns: mapRows.length ? "minmax(0,1.4fr) minmax(0,1fr)" : "1fr",
        gap: 14,
        alignItems: "stretch"
      }
    }, mapRows.length > 0 && /*#__PURE__*/React.createElement(PostalBubbleMap, {
      rows: mapRows,
      showLabels: true,
      fill: true,
      height: 560,
      onClick: label => this.props.history.push(`/Campaigns/Markets/${encodeURIComponent(label)}`)
    }), /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Market"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Impr."), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Spend"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394"))), /*#__PURE__*/React.createElement("tbody", null, (data.byMarket || []).map(m => /*#__PURE__*/React.createElement("tr", {
      key: m.market,
      className: "clickable",
      onClick: () => this.props.history.push(`/Campaigns/Markets/${encodeURIComponent(m.market)}`)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, m.market), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, (m.channels || []).map(channelLabel).join(" · "))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(m.kpis.impressions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, pct(m.kpis.ctr)), money && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usd(m.kpis.spend)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(m.kpis.conversions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(DeltaTag, {
      cur: m.kpis.impressions,
      prev: m.compare && m.compare.impressions
    }))))))))), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "What changed"
    }, (() => {
      // biggest movers vs the comparison window — markets and campaigns together
      const cands = [...(data.byMarket || []).map(m => ({
        kind: "market",
        label: m.market,
        cur: m.kpis,
        prev: m.compare,
        go: () => this.props.history.push(`/Campaigns/Markets/${encodeURIComponent(m.market)}`)
      })), ...(data.topCampaigns || []).map(c => {
        // trim the channel token + trailing market from the title — the sub already shows them
        let t = campaignTitle(c);
        const mk = (c.market || "").trim();
        if (mk && t.toLowerCase().endsWith(mk.toLowerCase())) t = t.slice(0, -mk.length).trim();
        t = t.replace(/\b(Display|Retarget(ing)?|Search|Social|Video|TV|CTV\/?OTT|Pinterest)$/i, "").trim();
        return {
          kind: "campaign",
          label: t || campaignTitle(c),
          sub: `${channelLabel(c.type)}${c.market ? " · " + c.market : ""}${c.source === "shopify" ? " · Imported" : ""}`,
          cur: c.kpis,
          prev: c.compare,
          go: dashOnly || c.source === "shopify" ? null : () => c.id && this.props.history.push(`/Campaigns/detail/${c.id}`)
        };
      })].map(x => {
        const p = Number((x.prev || {}).impressions) || 0,
          cu = Number((x.cur || {}).impressions) || 0;
        const conv = (Number((x.cur || {}).conversions) || 0) - (Number((x.prev || {}).conversions) || 0);
        return {
          ...x,
          delta: p > 0 ? (cu - p) / p * 100 : cu > 0 ? null : 0,
          abs: cu - p,
          conv
        };
      }).filter(x => x.delta !== null && (Number((x.prev || {}).impressions) || 0) >= 500);
      const up = cands.filter(x => x.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 3);
      const down = cands.filter(x => x.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 3);
      const line = (x, i) => /*#__PURE__*/React.createElement("div", {
        key: x.kind + x.label + i,
        className: x.go ? "clickable" : "",
        onClick: x.go || undefined,
        style: {
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          padding: "5px 0",
          borderBottom: "1px solid var(--plk-border,#eef0f3)",
          cursor: x.go ? "pointer" : "default"
        }
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12.5,
          fontWeight: 600,
          color: x.delta >= 0 ? "#217a4b" : "#b3383f",
          minWidth: 58
        }
      }, x.delta >= 0 ? "▲" : "▼", " ", Math.abs(x.delta).toFixed(0), "%"), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          flex: "1 1 auto"
        }
      }, x.label, x.sub ? /*#__PURE__*/React.createElement("span", {
        className: "code",
        style: {
          marginLeft: 6
        }
      }, x.sub) : /*#__PURE__*/React.createElement("span", {
        className: "code",
        style: {
          marginLeft: 6
        }
      }, "market")), /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0,
          fontSize: 11.5
        }
      }, num(Math.abs(x.abs)), " impr", x.conv ? ` · ${x.conv > 0 ? "+" : ""}${x.conv} conv` : ""));
      if (!data.compare) return /*#__PURE__*/React.createElement("p", {
        className: "territory-hint"
      }, "Pick a comparison in the top bar to see movers.");
      if (up.length === 0 && down.length === 0) return /*#__PURE__*/React.createElement("p", {
        className: "territory-hint"
      }, "No meaningful movement vs ", data.compare.from, " \u2013 ", data.compare.to, ".");
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: "0 0 4px"
        }
      }, "vs ", data.compare.from, " \u2013 ", data.compare.to, " \xB7 impressions, markets and campaigns with 500+ prior impressions"), up.map(line), down.map(line));
    })()))), !dashOnly && (data.topFlights || []).length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Top flights"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Flight"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Channels"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Markets"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Impressions"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Spend"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394 impr."))), /*#__PURE__*/React.createElement("tbody", null, data.topFlights.map((f, fi) => {
      const video = (f.channels || []).every(c => ["YouTube", "CTV"].includes(c));
      return /*#__PURE__*/React.createElement("tr", {
        key: `${f.id || 0}-${fi}`,
        className: "clickable",
        onClick: () => this.props.history.push(`/Campaigns/flight/${f.id}`)
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, String(f.name || "").replace(/_+/g, " ")), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, [f.startDate ? new Date(f.startDate).toLocaleDateString() + (f.endDate ? ` → ${new Date(f.endDate).toLocaleDateString()}` : "") : "", `${f.cells} cells`].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: {
          background: /running|active/i.test(f.status) ? "#e5f3ec" : "#eef0f3",
          color: /running|active/i.test(f.status) ? "#217a4b" : "#55606e"
        }
      }, f.status)), /*#__PURE__*/React.createElement("td", null, (f.channels || []).map(c => /*#__PURE__*/React.createElement("span", {
        key: c,
        className: "code",
        style: {
          marginRight: 4
        }
      }, channelLabel(c)))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, (f.markets || []).length), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(f.kpis.impressions)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, video ? "—" : num(f.kpis.clicks)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, video ? "—" : pct(f.kpis.ctr)), money && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, usd(f.kpis.spend)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(f.kpis.conversions)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, /*#__PURE__*/React.createElement(DeltaTag, {
        cur: f.kpis.impressions,
        prev: f.compare && f.compare.impressions
      })));
    })))))), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Top campaigns"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Campaign"), /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", null, "Market"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Impressions"), anyVideoTop && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Video views"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Spend"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394 impr."))), /*#__PURE__*/React.createElement("tbody", null, (data.topCampaigns || []).map((c, ci) => /*#__PURE__*/React.createElement("tr", {
      key: `${c.id || 0}-${c.type}-${c.market}-${ci}`
    }, /*#__PURE__*/React.createElement("td", null, dashOnly || c.source === "shopify" ? /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, campaignTitle(c)) : /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => c.id && this.props.history.push(`/Campaigns/detail/${c.id}`)
    }, campaignTitle(c)), c.source === "shopify" && /*#__PURE__*/React.createElement("span", {
      className: "code",
      title: "Imported from Shopify Marketing \u2014 sessions, orders and revenue measured by Plunk from site traffic; spend, impressions and clicks are not shared by Shopify",
      style: {
        marginLeft: 6,
        background: "#eef0f3",
        color: "#55606e"
      }
    }, "Imported \xB7 ", c.app || "Shopify")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, channelLabel(c.type))), /*#__PURE__*/React.createElement("td", null, c.market && c.market !== "All" ? /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      title: "Open this market's campaigns",
      onClick: () => this.props.history.push(`/Campaigns/Markets/${encodeURIComponent(c.market)}`)
    }, c.market) : c.market || "—"), /*#__PURE__*/React.createElement("td", null, c.status ? /*#__PURE__*/React.createElement("span", {
      className: "cust-stage",
      style: {
        background: isActiveStatus(c.status) ? "#e5f3ec" : "#eef0f3",
        color: isActiveStatus(c.status) ? "#217a4b" : "#8a94a6"
      }
    }, isActiveStatus(c.status) ? "Active" : c.status) : Number(c.id) > 0 ? "—" : /*#__PURE__*/React.createElement("span", {
      className: "dim",
      title: "Results for this campaign arrive from the ad platform, but it isn't linked to a campaign in Plunk, so its on/off status isn't known here"
    }, "Not linked")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.kpis.impressions)), anyVideoTop && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, Number(c.kpis.videoViews) > 0 ? num(c.kpis.videoViews) : "—"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, ["YouTube", "CTV/OTT"].includes(c.type) ? "—" : num(c.kpis.clicks)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, ["YouTube", "CTV/OTT"].includes(c.type) ? "—" : pct(c.kpis.ctr)), money && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usd(c.kpis.spend)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.kpis.conversions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(DeltaTag, {
      cur: c.kpis.impressions,
      prev: c.compare && c.compare.impressions
    })))))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        marginBottom: 0
      }
    }, "Top 10 by impressions", !dashOnly && /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Campaigns/Flights")
    }, "all campaigns and flights \u2192"))))))));
  }
}
const Routable = withRouter(OverviewView);
export default class CampaignsOverviewViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}